import React, { useCallback, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { PromotionDialog } from './components/PromotionDialog';
import { usePromotions, useTariffs, useActivatePromotion, useDeactivatePromotion } from './hooks';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { SortType } from '../../../types/pagination';
import { Promotion, serviceTypeLabels } from '../../../types/promotion';
import { parseApiError, isErrorResponse } from '../../../utils/apiErrors';

const formatDateRange = (startDate: string, endDate: string | null): string => {
  const start = moment(startDate).format('DD.MM.YYYY');

  if (!endDate) {
    return `с ${start} (бессрочно)`;
  }

  const end = moment(endDate).format('DD.MM.YYYY');

  return `${start} – ${end}`;
};

export const Promotions: React.FC = () => {
  const { data, mutate: fetchPromotions, isPending: isLoading } = usePromotions();
  const { data: tariffs = [] } = useTariffs();
  const { mutate: activatePromotion, isPending: isActivating } = useActivatePromotion();
  const { mutate: deactivatePromotion, isPending: isDeactivating } = useDeactivatePromotion();
  const { enqueueSnackbar } = useSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromotionId, setEditingPromotionId] = useState<number | undefined>();
  const [processingId, setProcessingId] = useState<number | null>(null);

  const handleAddPromotion = useCallback(() => {
    setEditingPromotionId(undefined);
    setDialogOpen(true);
  }, []);

  const handleEditPromotion = useCallback((item: Promotion) => {
    setEditingPromotionId(item.id);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingPromotionId(undefined);
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    fetchPromotions({ pageNumber: 0, pageSize: 25, sortFieldName: '', sortDirection: SortType.Ask });
  }, [handleCloseDialog, fetchPromotions]);

  const refreshList = useCallback(() => {
    fetchPromotions({ pageNumber: 0, pageSize: 25, sortFieldName: '', sortDirection: SortType.Ask });
  }, [fetchPromotions]);

  const handleActivate = useCallback(
    (promotion: Promotion) => {
      setProcessingId(promotion.id);
      activatePromotion(promotion.id, {
        onSettled: () => setProcessingId(null),
        onSuccess: (result) => {
          if (isErrorResponse(result)) {
            enqueueSnackbar(parseApiError(result), { variant: 'error' });
            return;
          }
          refreshList();
        },
        onError: (err) => {
          enqueueSnackbar(parseApiError(err), { variant: 'error' });
        },
      });
    },
    [activatePromotion, refreshList, enqueueSnackbar],
  );

  const handleDeactivate = useCallback(
    (promotion: Promotion) => {
      setProcessingId(promotion.id);
      deactivatePromotion(promotion.id, {
        onSettled: () => setProcessingId(null),
        onSuccess: (result) => {
          if (isErrorResponse(result)) {
            enqueueSnackbar(parseApiError(result), { variant: 'error' });
            return;
          }
          refreshList();
        },
        onError: (err) => {
          enqueueSnackbar(parseApiError(err), { variant: 'error' });
        },
      });
    },
    [deactivatePromotion, refreshList, enqueueSnackbar],
  );

  const columns = useMemo<MRT_ColumnDef<Promotion>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Наименование',
        filterVariant: 'text',
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'serviceType',
        header: 'Услуга',
        filterVariant: 'select',
        filterSelectOptions: Object.values(serviceTypeLabels),
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        size: 180,
        Cell: ({ cell }) => {
          const serviceType = cell.row.original.serviceType;
          return <span>{serviceTypeLabels[serviceType]}</span>;
        },
      },
      {
        accessorFn: (originalRow) => formatDateRange(originalRow.startDate, originalRow.endDate),
        id: 'dateRange',
        header: 'Срок действия',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        size: 200,
      },
      {
        accessorFn: (originalRow) => (originalRow.active ? 'Активна' : 'Неактивна'),
        id: 'active',
        header: 'Статус',
        filterVariant: 'select',
        filterSelectOptions: ['Активна', 'Неактивна'],
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        size: 120,
        Cell: ({ cell }) => (
          <span className={cell.row.original.active ? 'grow' : 'fall'}>
            {cell.row.original.active ? 'Активна' : 'Неактивна'}
          </span>
        ),
      },
      {
        accessorKey: 'clients',
        header: 'Участников',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        size: 120,
      },
      {
        id: 'toggle',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 50,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const now = moment();
          const startInFuture = moment(original.startDate).isAfter(now);
          const endInFuture = !original.endDate || moment(original.endDate).isAfter(now);
          const isProcessing = processingId === original.id && (isActivating || isDeactivating);

          if (isProcessing) {
            return <CircularProgress size={20} />;
          }

          if (original.active && startInFuture) {
            return (
              <Tooltip title="Деактивировать">
                <PauseCircleOutlineRoundedIcon
                  sx={{ cursor: 'pointer', marginTop: 0.5, color: 'warning.main' }}
                  onClick={() => handleDeactivate(original)}
                />
              </Tooltip>
            );
          }

          if (!original.active && endInFuture) {
            return (
              <Tooltip title="Активировать">
                <PlayCircleOutlineRoundedIcon
                  sx={{ cursor: 'pointer', marginTop: 0.5, color: 'success.main' }}
                  onClick={() => handleActivate(original)}
                />
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        accessorKey: 'edit',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Tooltip title="Редактировать">
            <EditNoteRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5 }}
              onClick={() => handleEditPromotion(original)}
            />
          </Tooltip>
        ),
      },
    ],
    [handleEditPromotion, handleActivate, handleDeactivate, processingId, isActivating, isDeactivating],
  );

  return (
    <>
      <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={handleAddPromotion}>
        Добавить
      </Button>

      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="administration-promotions"
          serverMode
          columns={columns}
          data={data?.pageItems || []}
          totalRowCount={data?.total || 0}
          isLoading={isLoading}
          pageSize={25}
          onFetchData={fetchPromotions}
        />
      </div>

      {dialogOpen && (
        <PromotionDialog
          promotionId={editingPromotionId}
          tariffs={tariffs}
          onClose={handleCloseDialog}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
};
